import FlexBox from "lib/components/common/flex-box";
import SimpleButton from "lib/components/common/simple-button";
import SimpleCard from "lib/components/common/simple-card";
import SimpleDataTable from "lib/components/common/simple-data-table";
import SimpleDropdown from "lib/components/common/simple-dropdown";
import SimpleModal from "lib/components/common/simple-modal";
import SimpleTextbox from "lib/components/common/simple-textbox";
import { DEFAULT_PRACTITIONER_THERAPY_VALUES } from "lib/constants/default-values";
import { PRACTITIONER_THERAPIES_COLUMNS } from "lib/constants/table-columns";
import useFormBinder from "lib/hooks/useFormBinder";
import {
  getArrayOfValuesFromArray,
  getItemFromArray,
} from "lib/utils/array-utils";
import { FC, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { PiPlusBold } from "react-icons/pi";

interface ProfilePicCardProps {
  isLoading?: boolean;
  isEditable?: boolean;
  allTherapies: Therapy[];
  therapies?: PractitionerTherapy[];
  onAddUpdateTherapies?: (values: PractitionerTherapy[]) => void;
  onDeleteTherapies?: (values: PractitionerTherapy) => void;
}

const PractitionerTherapiesCard: FC<ProfilePicCardProps> = ({
  isLoading,
  isEditable,
  allTherapies,
  therapies,
  onAddUpdateTherapies,
  onDeleteTherapies,
}) => {
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [practitionerTherapies, setPractitionerTherapies] = useState<
    PractitionerTherapy[]
  >(therapies ?? []);
  const [validationError, setValidationError] = useState<string>("");

  const { handleChange, values, resetValues } = useFormBinder({
    ...DEFAULT_PRACTITIONER_THERAPY_VALUES,
    therapyId: allTherapies[0]?.id,
  } as PractitionerTherapy);

  useEffect(() => {
    if (therapies) {
      setPractitionerTherapies(therapies);
    }
  }, [therapies]);

  const closeModal = () => {
    setIsEditing(false);
    setIsShowModal(false);
    setValidationError(""); // Clear validation error on close
  };

  const openModal = () => {
    resetValues();
    setIsShowModal(true);
    setValidationError(""); // Clear validation error on open
  };

  const handleDeleteTherapy = (therapyId: string) => {
    setIsEditing(false);
    const foundItem = getItemFromArray(
      practitionerTherapies,
      "therapyId",
      therapyId
    );
    if (foundItem) {
      onDeleteTherapies?.(foundItem);
    }
    setPractitionerTherapies(
      practitionerTherapies.filter((item) => item.therapyId !== therapyId)
    );
    resetValues();
  };

  const handleAddTherapy = (therapy: PractitionerTherapy) => {
    setIsEditing(false);
    setValidationError("");
    // Validation: all fields required
    if (!therapy.therapyId || !therapy.sessionTime || !therapy.perSessionPrice) {
      setValidationError("All fields are mandatory.");
      return;
    }

    const foundItem = getItemFromArray(
      practitionerTherapies,
      "therapyId",
      therapy.therapyId
    );

    const therapyItem = getItemFromArray(allTherapies, "id", therapy.therapyId);

    const newTherapy = {
      ...therapy,
      name: therapyItem?.name,
    } as PractitionerTherapy;

    onAddUpdateTherapies?.([newTherapy]);

    if (foundItem) {
      setPractitionerTherapies(
        practitionerTherapies.map((item) =>
          item.therapyId === foundItem.therapyId ? newTherapy : item
        )
      );
      resetValues();
      return;
    }
    setPractitionerTherapies([...practitionerTherapies, newTherapy]);
    resetValues();
  };

  return (
    <>
      <SimpleCard
        title="Practice"
        isTitleNoBg
        action={
          isEditable && (
            <SimpleButton isLoading={isLoading} onClick={openModal}>
              <FlexBox>
                <>Add</> <PiPlusBold />
              </FlexBox>
            </SimpleButton>
          )
        }
      >
        <SimpleDataTable
          className="mt-3"
          data={practitionerTherapies}
          columns={PRACTITIONER_THERAPIES_COLUMNS}
          isShowPagination={false}
          onRowClick={(selectedTherapy) => {
            if (!isEditable) return;
            resetValues(selectedTherapy as PractitionerTherapy);
            setIsEditing(true);
            setIsShowModal(true);
          }}
        />
      </SimpleCard>
      <SimpleModal
        size="lg"
        title={isEditing ? "Edit Details" : "Add Details"}
        open={isShowModal}
        close={closeModal}
        footer={
          <FlexBox className="w-100">
            <SimpleButton
              variant="outline-danger"
              onClick={() => {
                handleDeleteTherapy(values.therapyId);
                setIsShowModal(false);
              }}
              disabled={!isEditing}
            >
              Delete
            </SimpleButton>
            <SimpleButton
              isLoading={isLoading}
              onClick={() => {
                handleAddTherapy(values);
                if (!values.therapyId || !values.sessionTime || !values.perSessionPrice) return;
                setIsShowModal(false);
              }}
            >
              Add
            </SimpleButton>
          </FlexBox>
        }
      >
        <Form>
          {validationError && (
            <div style={{ color: 'red', marginBottom: 8 }}>{validationError}</div>
          )}
          <SimpleDropdown
            label="Therapy"
            options={getArrayOfValuesFromArray(allTherapies, "name")}
            values={getArrayOfValuesFromArray(allTherapies, "id")}
            selectedValue={values.therapyId}
            onChange={handleChange}
            name="therapyId"
          />
          <SimpleTextbox
            type="number"
            label="Ideal Session Time"
            value={values.sessionTime}
            onChange={handleChange}
            name="sessionTime"
            suffix="minutes"
          />
          <SimpleTextbox
            type="number"
            label="Per Session Cost"
            value={values.perSessionPrice}
            onChange={handleChange}
            name="perSessionPrice"
            prefix="£"
          />
        </Form>
      </SimpleModal>
    </>
  );
};

export default PractitionerTherapiesCard;
